import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { apis } from '@/modules/apiClients'
import { organizationsQueryKeys } from '@/storeHooks/useOrganizations'
import { useAuth } from './useAuth'
import { useOrganization } from './useOrganization'
import { toast } from 'sonner'
import { useTar } from '@/modules/i18n'
import { AxiosError } from 'axios'

type SettingName = 'invitationEmails' | 'submissionEmails'

export const useOrganizationSettings = () => {
  const { organizationId } = useAuth()
  const { organization } = useOrganization(organizationId)
  const queryClient = useQueryClient()
  const { t } = useTar()

  const getOrgSetting = useCallback(<T extends SettingName>(settingName: T): boolean | undefined => {
    return organization?.settings?.[settingName]
  }, [organization?.settings])

  const setOrgSetting = useCallback(async <T extends SettingName>(
    settingName: T,
    value: boolean
  ): Promise<void> => {
    if (!organizationId) return

    try {
      await apis.partnerService.organizationsOrganizationIdSettingsPatch(
        organizationId,
        {
          settings: {
            [settingName]: value,
          },
        }
      )
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: organizationsQueryKeys.all() })
      toast.success(t('organizationSettings.settingsUpdated'))
    } catch (error) {
      const errorMessage = error instanceof AxiosError
        ? error.response?.data?.error || error.message
        : 'Failed to update settings'
      toast.error(errorMessage)
      throw error
    }
  }, [organizationId, queryClient, t])

  return {
    getOrgSetting,
    setOrgSetting,
    isLoading: !organization,
    settings: organization?.settings,
  }
}
